import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { Box, useMediaQuery, CircularProgress } from "@mui/material";
import api from "../../utils/api"; // Assumed path
import OrdersPreview from "../OrdersPreview";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import { PermissionsContext } from "../../utils/PermissionsContext";

const Home = () => {
  const [userFullName, setUserFullName] = useState("User");
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [openPropertiesMenu, setOpenPropertiesMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);

  const isMobile = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? 80 : 250;

  // Fetch user roles and permissions
  useEffect(() => {
    const init = async () => {
      if (!localStorage.getItem("authToken")) {
        navigate("/login");
        return;
      }

      const roleId = localStorage.getItem("roleId");
      const firstName = localStorage.getItem("firstName");
      const lastName = localStorage.getItem("lastName");
      const storedRoles = JSON.parse(localStorage.getItem("roles") || "[]");
      const username = localStorage.getItem("username");

      if (!username) return;

      setUserFullName(`${firstName} ${lastName}`);
      setRoles(storedRoles);

      try {
        const response = await api.get("/auth/permissions", {
          headers: { "x-roleid": roleId },
        });
        setPermissions(response.data.permissions || []);
      } catch (err) {
        console.error("Error fetching permissions", err);
      } finally {
        setLoadingPermissions(false);
      }
    };

    init();
  }, [navigate, location.pathname]);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const lastChecked = new Date(localStorage.getItem("lastChecked") || new Date().toISOString());
        const response = await api.get(`/getNotifications?hours=1`);
        const notificationsResponse = response.data.notifications || [];

        const newNotifications = notificationsResponse.filter((n) => new Date(n.date) > lastChecked);
        if (newNotifications.length > 0) {
          setNotifications((prev) => [...prev, ...newNotifications]);
          setUnreadCount((prev) => prev + newNotifications.length);

          const latestTimestamp = newNotifications.reduce(
            (latest, current) => new Date(current.date) > new Date(latest) ? current.date : latest,
            lastChecked
          );
          localStorage.setItem("lastChecked", new Date(latestTimestamp).toISOString());
        }
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const hasPermission = (perm) => permissions.includes(perm);
  const isActive = (path) => location.pathname === path;

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleSignOut = () => {
    localStorage.clear();
    navigate("/login");
  };

  const openSettingsMenu = (event) => setSettingsAnchorEl(event.currentTarget);
  const closeSettingsMenu = () => setSettingsAnchorEl(null);
const handleSystemPreferences = () => {
    navigate("/home/system-preferences");
    closeSettingsMenu();
  };
  const handleUserProfile = () => {
    navigate("/home/user-profile");
    closeSettingsMenu();
  };
  const handleThemeSettings = () => {
    navigate("/home/theme-settings");
    closeSettingsMenu();
  };


  const openNotificationsMenu = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
    setUnreadCount(0);
    localStorage.setItem("lastChecked", new Date().toISOString());
  };
  const closeNotificationsMenu = () => setNotificationsAnchorEl(null);

  const pageTitles = {
    "/home": "Dashboard",
    "/home/users": "Users",
    "/home/coverletters": "Cover Letters",
    "/home/properties": "Properties",
    "/home/architecturalrequest": "Architectural Request",
    "/home/workorders": "Work Orders",
    "/home/members": "Members",
    "/home/filemanager": "File Manager",
    "/home/violations": "Violations",
    "/home/reports": "Reports",
    "/home/integrations": "Integrations",
  };

  const pageTitle = pageTitles[location.pathname] || "MY HOA";

  // ⏳ Show loading screen while fetching permissions
  if (loadingPermissions) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "#f9f9f9",
        }}
      >
        <CircularProgress size={48} thickness={4} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", width: "100%", height: "100vh" }}>
      {/* Orders Preview */}
      <OrdersPreview />

      {/* Sidebar Component */}
      <Sidebar
        userFullName={userFullName}
        openPropertiesMenu={openPropertiesMenu}
        setOpenPropertiesMenu={setOpenPropertiesMenu}
        hasPermission={hasPermission}
        isActive={isActive}
        handleSignOut={handleSignOut}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          marginLeft: `${sidebarWidth}px`,
          transition: "margin-left 0.3s ease",
          display: "flex",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        {/* TopBar Component */}
        <TopBar
          pageTitle={pageTitle}
          isFullscreen={isFullscreen}
          toggleFullscreen={toggleFullscreen}
          hasPermission={hasPermission}
          notifications={notifications}
          notificationsAnchorEl={notificationsAnchorEl}
          openNotificationsMenu={openNotificationsMenu}
          closeNotificationsMenu={closeNotificationsMenu}
          unreadCount={unreadCount}
          settingsAnchorEl={settingsAnchorEl}
          openSettingsMenu={openSettingsMenu}
          closeSettingsMenu={closeSettingsMenu}
           handleUserProfile={handleUserProfile}
          handleSystemPreferences={handleSystemPreferences}
          handleThemeSettings={handleThemeSettings}
        />

        {/* Permissions Context for nested routes */}
        <PermissionsContext.Provider value={permissions}>
          <Box sx={{ padding: "2rem", overflowY: "auto", flexGrow: 1 }}>
            <Outlet />
          </Box>
        </PermissionsContext.Provider>
      </Box>
    </Box>
  );
};

export default Home;
